import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import AddCustomerModal from './AddCustomerModal'; 
import CustomerDetailView from './CustomerDetailView'; 

const CustomerManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Modals ki states
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/customers?search=${search}&page=${page}`);
            if (res.data.success) {
                setCustomers(res.data.customers);
                setTotalPages(res.data.totalPages);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCustomers();
        }, 500); 
        return () => clearTimeout(delayDebounceFn);
    }, [search, page]);

    return (
        <div style={styles.container}>
            {/* Top Bar: Search & Add Button */}
            <div style={styles.topBar}>
                <div style={styles.searchWrapper}>
                    <input 
                        type="text" 
                        placeholder="Search Name, CNIC, Phone or Ref No..." 
                        style={styles.searchInput}
                        value={search}
                        onChange={(e) => {setSearch(e.target.value); setPage(1);}}
                    />
                    {loading && <span style={styles.loaderText}>Searching...</span>}
                </div>
                <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>
                    + Add New Customer
                </button>
            </div>

            {/* Table Card with Modern Professional Styling */}
            <div style={styles.tableCard}>
                <table style={styles.table}>
                    <thead>
                        <tr style={styles.theadRow}>
                            <th style={{...styles.th, width: '12%'}}>Ref No</th>
                            <th style={{...styles.th, width: '28%'}}>Customer Info</th>
                            <th style={{...styles.th, width: '20%'}}>CNIC</th>
                            <th style={{...styles.th, width: '15%'}}>Phone</th>
                            <th style={{...styles.th, width: '15%'}}>City</th>
                            <th style={{...styles.th, width: '10%', textAlign: 'center'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.length > 0 ? customers.map(c => (
                            <tr key={c.id} style={styles.tr}>
                                <td style={styles.td}>
                                    <span style={styles.refBadge}>{c.ref_no || 'N/A'}</span>
                                </td>
                                <td style={styles.td}>
                                    <div style={{fontWeight: '600', color: '#0f172a'}}>{c.full_name}</div>
                                    <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>{c.father_name}</div>
                                </td>
                                <td style={{...styles.td, fontFamily: 'monospace', color: '#475569'}}>{c.cnic}</td>
                                <td style={{...styles.td, fontFamily: 'monospace', color: '#475569'}}>{c.phone}</td>
                                <td style={styles.td}>{c.city_name || '---'}</td>
                                <td style={{...styles.td, textAlign: 'center'}}>
                                    <button 
                                        style={styles.viewBtn} 
                                        onClick={() => setSelectedCustomer(c)}
                                        onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.target.style.background = '#ffffff'}
                                    >
                                        👁️ View
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" style={{padding: '50px', textAlign: 'center', color: '#94a3b8', fontSize: '14px'}}>
                                    No customers found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div style={styles.pagination}>
                <button 
                    disabled={page === 1} 
                    onClick={() => setPage(p => p - 1)} 
                    style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                >
                    Previous
                </button>
                <span style={styles.pageInfo}>Page <b>{page}</b> of <b>{totalPages}</b></span>
                <button 
                    disabled={page === totalPages} 
                    onClick={() => setPage(p => p + 1)} 
                    style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                >
                    Next
                </button>
            </div>

            {/* Modals */}
            {showAddModal && (
                <AddCustomerModal 
                    onClose={() => setShowAddModal(false)} 
                    onRefresh={fetchCustomers} 
                />
            )}

            {selectedCustomer && (
                <CustomerDetailView 
                    customer={selectedCustomer} 
                    onClose={() => setSelectedCustomer(null)} 
                    onCustomerUpdated={fetchCustomers} 
                />
            )}
        </div>
    );
};

const styles = {
    container: { animation: 'fadeIn 0.3s ease-in', padding: '10px' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', gap: '15px' },
    searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchInput: { padding: '12px 18px', width: '400px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    loaderText: { position: 'absolute', right: '15px', fontSize: '12px', color: '#3b82f6', fontWeight: '500' },
    addBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)', transition: 'background 0.2s' },
    tableCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02)' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    theadRow: { background: '#f8fafc', borderBottom: '2px solid #e2e8f0' },
    th: { padding: '16px 20px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s ease' },
    td: { padding: '16px 20px', fontSize: '14px', color: '#334155', verticalAlign: 'middle' },
    refBadge: { background: '#eff6ff', color: '#1d4ed8', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #bfdbfe' },
    viewBtn: { padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', cursor: 'pointer', fontSize: '13px', fontWeight: '500', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', transition: 'all 0.2s' },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '25px', gap: '15px' },
    pageBtn: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '500', color: '#334155', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' },
    pageBtnDisabled: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #f1f5f9', background: '#f8fafc', color: '#cbd5e1', cursor: 'not-allowed' },
    pageInfo: { fontSize: '14px', color: '#64748b' }
};

export default CustomerManagement;